import React from 'react';
import { Coffee, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBreakReminder } from '../../contexts/BreakReminderContext';

const getActiveDaysText = (activeDays, t, daysShort) => {
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const activeDayIndices = dayKeys
    .map((key, index) => (activeDays[key] ? index : -1))
    .filter((index) => index !== -1);
  if (activeDayIndices.length === 7) return t('activeDaysAll');
  if (activeDayIndices.length === 5 && activeDayIndices.every((i) => i < 5)) return t('activeDaysWeekdays');
  if (activeDayIndices.length === 0) return t('activeDaysNone');
  return activeDayIndices.map((i) => daysShort[i]).join(', ');
};

/**
 * Apple-style toggle switch
 */
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
      ${checked ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
        transition duration-200 ease-in-out
        ${checked ? 'translate-x-5' : 'translate-x-0.5'}
      `}
    />
  </button>
);

const BreakReminderBentoGrid = () => {
  const { t } = useTranslation('breaks');
  const daysShort = t('daysShort', { returnObjects: true });
  const { breaks, selectedBreak, selectBreak, updateBreak } = useBreakReminder();

  if (breaks.length === 0) return null;

  const handleToggle = (breakItem) => {
    updateBreak({
      ...breakItem,
      soundEnabled: !breakItem.soundEnabled,
    });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
      {breaks.map((breakItem) => {
        const isSelected = selectedBreak?.id === breakItem.id;
        const workMins = 45; // Display convention: "45 Dk Çalışma / X Dk Mola"
        const breakMins = breakItem.durationMinutes || 15;

        return (
          <div
            key={breakItem.id}
            onClick={() => selectBreak(breakItem)}
            className={`
              bg-white dark:bg-[var(--color-bg-elevated)] border border-slate-200 dark:border-white/10 rounded-xl p-6
              shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer
              ${isSelected ? 'ring-2 ring-blue-500 dark:ring-blue-500' : ''}
            `}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[#161f31] flex items-center justify-center">
                    <Coffee className="w-5 h-5 text-slate-600 dark:text-[var(--color-text-secondary)]" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                    {breakItem.name || t('bentoGrid.defaultName')}
                  </h3>
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-[var(--color-text-secondary)] mb-1">
                  {t('bentoGrid.workBreakLabel', { work: workMins, breakMin: breakMins })}
                </p>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-[var(--color-text-secondary)]">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{t('bentoGrid.startLabel')}: {breakItem.startTime || '10:00'}</span>
                  <span className="text-slate-400 dark:text-[var(--color-text-secondary)]">•</span>
                  <span>{getActiveDaysText(breakItem.activeDays || {}, t, daysShort)}</span>
                </div>
              </div>
              <div className="flex items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <Toggle
                  checked={!!breakItem.soundEnabled}
                  onChange={() => handleToggle(breakItem)}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BreakReminderBentoGrid;
